import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export type DropdownComponentWithSearchOption = {
  label: string;
  value: string;
};
export interface DropdownComponentProps {
  items: DropdownComponentWithSearchOption[];
  selectedItem?: DropdownComponentWithSearchOption;
  placeHolder?: string;
  setSelectedItem: (item: DropdownComponentWithSearchOption) => void;
  enableSearch?: boolean;
}

const DropdownComponentWithSearch: React.FC<DropdownComponentProps> = ({
  items,
  selectedItem,
  setSelectedItem,
  enableSearch,
  placeHolder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Conditionally filter items if search is enabled
  const filteredItems = enableSearch
    ? items.filter((item) =>
        item?.label?.toLowerCase()?.includes(searchText?.toLowerCase())
      )
    : items;

  const handleDocumentClick = (e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        className="flex justify-between items-center block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-[#fff] text-left focus:ring-indigo-500 focus:border-[#CED1D2] sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedItem ? selectedItem.label : placeHolder}
        <span className="float-right">
          <ChevronDown
            className={`w-4 h-4 transition-transform transform ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}
            color="red"
          />
        </span>
      </button>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 pt-1 bg-[#fff] border border-gray-300 rounded-md shadow-lg">
          {enableSearch && (
            <input
              type="text"
              placeholder="Search by name"
              className="block w-full px-3 py-2 border-b border-gray-300 sm:text-sm"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          )}
          <ul className="h-40 max-h-40 overflow-y-auto bg-white">
            {filteredItems.map((item, index) => (
              <li
                key={index}
                className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                  selectedItem?.value === item.value ? 'bg-indigo-100' : ''
                }`}
                onClick={() => {
                  setSelectedItem(item);
                  setIsOpen(false);
                }}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DropdownComponentWithSearch;
